// Typed analytics helper wrapping PostHog + Meta Pixel + first-party Supabase
// event log. Fails safely when keys/ids are absent (local preview, unconfigured
// env). Never breaks the app. Respects the CookieConsent choice — marketing
// pixels only load with "all"; the first-party Supabase log runs once the user
// has made ANY consent choice (essential or all).

import posthog from "posthog-js";
import { getCookieConsent } from "@/components/site/CookieConsent";
import { supabase } from "@/integrations/supabase/client";
import { attributionSuperProps } from "@/lib/marketing/attribution";

export type AnalyticsEvent =
  | "hero_cta_click"
  | "release_play"
  | "release_streaming_click"
  | "streaming_click"
  | "activation_start"
  | "newsletter_submit"
  | "contact_submit"
  | "outbound_click"
  | "player_play"
  | "player_queue"
  | "purchase"
  | "membership_start"
  | "product_view"
  | "bundle_view"
  | "checkout_start"
  | "download_requested"
  | "access_code_redeemed"
  | "library_view"
  | "order_view"
  | "wisdom_like"
  | "wisdom_save"
  | "wisdom_share"
  | "book_share"
  | "book_copy_link"
  | "book_external_click"
  | "book_purchase_click"
  | "book_read_full_edition"
  | "hero_signature_activation"
  | "field_surge"
  | "field_ankh_signature"
  | "field_idle_enter"
  | "field_attention"
  | "first_gate_breath"
  | "daily_gate_view"
  | "daily_gate_click"
  | "method_view"
  | "pillar_view"
  | "pillar_engage"
  | "gate_view"
  | "gate_engage"
  | "knowledge_click"
  | "chapter_open"
  | "chapter_complete"
  | "library_open"
  | "continue_reading_click"
  | "haptic_fire"
  | "auth_signin_success"
  | "auth_signin_failed"
  | "auth_signup"
  | "auth_password_reset_requested"
  | "pwa_install_prompt_shown"
  | "pwa_install_choice"
  | "pwa_install_dismissed"
  | "pwa_install_completed"
  | "download_page_view"
  | "download_platform_detected"
  | "android_apk_download_started"
  | "android_apk_download_completed"
  | "app_update_available"
  | "app_update_installed"
  | "ai_chat_started"
  | "ai_message_sent"
  | "ai_response_completed"
  | "ai_response_error"
  | "ai_mode_selected"
  | "ai_source_opened"
  | "ai_route_opened"
  | "ai_lesson_recommended"
  | "ai_quiz_generated"
  | "ai_memory_saved"
  | "ai_feedback_submitted"
  | "ai_response_stopped"
  | "ai_response_regenerated"
  | "ai_message_copied"
  | "home_view"
  | "home_path_click"
  | "membership_page_view"
  | "membership_manage_click"
  | "membership_plan_select"
  | "checkout_start"
  | "checkout_complete"
  | "signup_start"
  | "upgrade_prompt_click"
  | "newsletter_prefs_saved"
  | "dashboard_view"
  | "dashboard_continue_learning"
  | "lesson_start"
  | "lesson_complete"
  | "activation_play"
  | "music_interaction"
  | "press_kit_view"
  | "press_kit_asset_download"
  | "press_kit_copy"
  | "press_email_click"
  | "press_resource_click"
  | "press_release_view"
  | "press_archive_view"
  | "press_release_kit_view"
  | "press_outbound_click"
  | "article_conversion_click"
  | "beat_search"
  | "beat_filter"
  | "beat_sort"
  | "beat_card_click"
  | "beat_related_click"
  | "beat_collection_view"
  | "release_view"
  | "release_purchase_click"
  | "release_presave_click"
  | "release_presave_submit"
  | "next_release_cta_click"
  // -- Phase 2B authority / measurement hooks -------------------------------
  | "organic_landing"
  | "internal_search"
  | "search_result_click"
  | "concept_relation_click"
  | "entity_profile_click"
  // -- Phase 3A visitor journey / conversion intelligence -------------------
  | "journey_returning_visit"
  | "journey_primary_cta"
  | "journey_secondary_cta"
  | "journey_next_step_view"
  | "journey_next_step_click"
  // -- Phase 3B first value + return loop ------------------------------------
  | "return_visit_day"
  | "return_continue_view"
  | "return_continue_click"
  | "return_prompt_view"
  | "return_prompt_click"
  | "return_prompt_dismiss"
  | "start_here_view"
  | "start_here_step_start"
  | "start_here_step_complete"
  | "start_here_complete"
  | "activation_complete"
  | "gate_complete"
  | "path_memory_cleared"
  // -- Marketing funnel layer (src/lib/marketing/analytics.ts) ---------------
  | "marketing_page_view"
  | "marketing_release_view"
  | "marketing_listen_click"
  | "marketing_presave_click"
  | "marketing_own_click"
  | "marketing_checkout_start"
  | "marketing_join_click"
  | "marketing_press_click"
  | "marketing_signup_success"
  | "marketing_contact_success";


type Props = Record<string, string | number | boolean | null | undefined>;

const META_PIXEL_ID = "1989911651235332";

let initialized = false;
let phEnabled = false;
let fbEnabled = false;

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

function loadMetaPixel(): void {
  if (fbEnabled || typeof window === "undefined") return;
  if (window.fbq) { fbEnabled = true; return; }
  // Standard Meta Pixel snippet, kept minimal.
  const n: any = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  n("init", META_PIXEL_ID);
  n("track", "PageView");
  fbEnabled = true;
}

/**
 * Public (publishable) PostHog project token for KAfrequency.
 *
 * This is the browser-side project token — the same value PostHog prints in
 * its own install snippet. It is designed to be public and can only write
 * events into the project; it can never read data. It is inlined here as a
 * default because the deployed production bundle is built by the hosting
 * pipeline, which does not consume the repository's `.env.production` file —
 * the token therefore folded away to `undefined` at build time and PostHog was
 * never initialized in production. `VITE_POSTHOG_KEY` (or the Lovable PostHog
 * connector variable) still wins when present, so the key stays overridable.
 *
 * NEVER put a personal/private PostHog API key (`phx_…`) here.
 */
export const PUBLIC_POSTHOG_TOKEN = "phc_sUPrM3co6tWQvCuDW9D4gJg9Y2ijsHWTZTGazS8tj8zm";
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/**
 * Resolves the public PostHog project token. Only client-safe, publishable
 * values are read here: the project token from the Lovable PostHog connector,
 * the project's own VITE_POSTHOG_KEY, or the inlined public default above.
 */
export function posthogConfig(): { key: string; host: string } | null {
  const key =
    import.meta.env.VITE_POSTHOG_KEY ||
    import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY ||
    PUBLIC_POSTHOG_TOKEN;
  if (!key) return null;
  const region = import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION;
  const host =
    import.meta.env.VITE_POSTHOG_HOST ||
    (region === "eu" ? "https://eu.i.posthog.com" : DEFAULT_POSTHOG_HOST);
  return { key, host };
}

/** Last URL reported as a $pageview — guards against duplicate captures. */
let lastPageViewUrl: string | null = null;

function currentUrl(path?: string): string {
  if (typeof window === "undefined") return path ?? "/";
  try {
    return new URL(path ?? window.location.pathname, window.location.origin).href;
  } catch {
    return window.location.href;
  }
}

/**
 * Sends exactly one `$pageview` per resolved URL. SPA route changes and the
 * initial load both come through here, so PostHog never double-counts.
 */
function capturePostHogPageView(path?: string): void {
  if (!phEnabled) return;
  const url = currentUrl(path);
  if (url === lastPageViewUrl) return;
  lastPageViewUrl = url;
  try {
    posthog.capture("$pageview", {
      $current_url: url,
      $pathname: typeof window === "undefined" ? path : new URL(url).pathname,
      ...attributionSuperProps(),
    });
  } catch {
    /* noop */
  }
}

/**
 * Initializes PostHog. Consent-gated: analytics/marketing cookies are only
 * accepted under the "all" choice, so nothing is initialized (and no network
 * call is made) on "essential only" or before a choice is made. Session replay
 * is explicitly disabled — the cookie policy does not cover it.
 *
 * Automatic pageview capture is OFF: this is a SPA, so pageviews are emitted
 * from `trackPageView()` on every route change instead. Capturing both would
 * double-count the first load.
 */
function initPostHog(): void {
  if (phEnabled) return;
  const config = posthogConfig();
  if (!config) return;
  try {
    posthog.init(config.key, {
      api_host: config.host,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
      disable_session_recording: true,
    });
    phEnabled = true;
    try {
      const props = attributionSuperProps();
      if (Object.keys(props).length > 0) posthog.register(props);
    } catch {
      /* noop */
    }
    // Consent can be granted after the first route render; capture the page
    // the visitor is on right now so the session is never blank.
    capturePostHogPageView();
  } catch (err) {
    console.warn("[analytics] posthog init failed", err);
  }
}

export function initAnalytics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // Load PostHog and the Meta Pixel only if the user accepted marketing/
  // analytics cookies.
  if (getCookieConsent() === "all") {
    initPostHog();
    loadMetaPixel();
  }

  window.addEventListener("ka:cookie-consent", (e) => {
    const detail = (e as CustomEvent).detail;
    if (detail === "all") {
      initPostHog();
      loadMetaPixel();
    }
  });
}


// -- First-party Supabase log ------------------------------------------------

const SESSION_KEY = "ka_analytics_sid";

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function logToSupabase(event: string, props?: Props, path?: string): void {
  if (typeof window === "undefined") return;
  // Only log after the user has made a consent decision. First-party
  // aggregate analytics runs on "essential" and "all"; skip when null.
  if (getCookieConsent() === null) return;
  const meta = props
    ? (Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined)) as Record<string, unknown>)
    : {};
  supabase
    .from("analytics_events")
    .insert({
      event,
      path: path ?? window.location.pathname,
      session_id: getSessionId(),
      referrer: document.referrer || null,
      meta: meta as never,
    })
    .then(() => undefined, () => undefined);
}

export function track(event: AnalyticsEvent, props?: Props): void {
  if (typeof window === "undefined") return;
  if (phEnabled) {
    try { posthog.capture(event, props ?? {}); } catch { /* noop */ }
  }
  if (fbEnabled && window.fbq) {
    try {
      const fbEventMap: Partial<Record<AnalyticsEvent, string>> = {
        purchase: "Purchase",
        membership_start: "Subscribe",
        newsletter_submit: "Lead",
        contact_submit: "Contact",
      };
      const fbName = fbEventMap[event];
      if (fbName) window.fbq("track", fbName, props ?? {});
      else window.fbq("trackCustom", event, props ?? {});
    } catch { /* noop */ }
  }
  logToSupabase(event, props);
}

/** Search/answer engines we count as organic entry sources. */
const ORGANIC_REFERRERS: Array<[RegExp, string]> = [
  [/(^|\.)google\./i, "google"],
  [/(^|\.)bing\./i, "bing"],
  [/duckduckgo\./i, "duckduckgo"],
  [/(^|\.)yahoo\./i, "yahoo"],
  [/(^|\.)ecosia\./i, "ecosia"],
  [/brave\.com/i, "brave"],
  [/(chatgpt|openai)\.com/i, "chatgpt"],
  [/perplexity\.ai/i, "perplexity"],
  [/(claude|anthropic)\./i, "claude"],
  [/gemini\.google\./i, "gemini"],
  [/copilot\.microsoft\./i, "copilot"],
];

let organicLandingLogged = false;

/**
 * Fires once per session when the entry page arrived from a search or answer
 * engine, so organic landing pages can be measured without extra tooling.
 */
function maybeTrackOrganicLanding(path: string): void {
  if (organicLandingLogged) return;
  organicLandingLogged = true;
  const ref = document.referrer;
  if (!ref) return;
  let host = "";
  try { host = new URL(ref).hostname; } catch { return; }
  if (host === window.location.hostname) return;
  const match = ORGANIC_REFERRERS.find(([re]) => re.test(host));
  if (!match) return;
  track("organic_landing", { path, engine: match[1], referrer_host: host });
}

export function trackPageView(path: string): void {
  if (typeof window === "undefined") return;
  maybeTrackOrganicLanding(path);
  capturePostHogPageView(path);
  if (fbEnabled && window.fbq) {
    try { window.fbq("track", "PageView"); } catch { /* noop */ }
  }
  logToSupabase("pageview", undefined, path);
}
